package com.dotdrives.client

import com.dotdrives.core.uuidV7
import com.dotdrives.model.NewReminder
import com.dotdrives.model.ReminderFilter
import com.dotdrives.model.ReminderSubjectOption
import com.dotdrives.model.ReminderSummary
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import io.github.jan.supabase.postgrest.query.filter.FilterOperator
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

class ReminderClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val authClient: AuthClient = AuthClient(client),
) {
    suspend fun list(orgId: String, filter: ReminderFilter, currentUserId: String): List<ReminderSummary> {
        val rows = client.from(REMINDERS_TABLE)
            .select {
                limit(LIST_LIMIT)
                filter {
                    eq("org_id", orgId)
                    when (filter) {
                        ReminderFilter.All -> filter("completed_at", FilterOperator.IS, null)
                        ReminderFilter.Mine -> {
                            filter("completed_at", FilterOperator.IS, null)
                            eq("assigned_to_user_id", currentUserId)
                        }
                        ReminderFilter.AutoCreated -> {
                            filter("completed_at", FilterOperator.IS, null)
                            filterNot("created_by_rule_id", FilterOperator.IS, null)
                        }
                        ReminderFilter.Recurring -> {
                            filter("completed_at", FilterOperator.IS, null)
                            filterNot("recurrence", FilterOperator.IS, null)
                        }
                        ReminderFilter.Done -> filterNot("completed_at", FilterOperator.IS, null)
                    }
                }
                if (filter == ReminderFilter.Done) {
                    order("completed_at", Order.DESCENDING)
                } else {
                    order("due_at", Order.ASCENDING)
                }
            }
            .decodeList<ReminderRow>()
        return enrich(orgId, rows)
    }

    suspend fun searchSubjects(orgId: String, query: String): List<ReminderSubjectOption> {
        val trimmed = query.trim()
        if (trimmed.isEmpty()) return emptyList()
        val givers = client.from(GIVERS_TABLE)
            .select {
                limit(SUBJECT_RESULTS_LIMIT)
                filter {
                    eq("org_id", orgId)
                    ilike("display_name", "%$trimmed%")
                }
            }
            .decodeList<GiverSubjectRow>()
            .map { ReminderSubjectOption("giver", it.id, it.displayName, "Giver · ${it.giverType}") }
        val drives = client.from(DRIVES_TABLE)
            .select {
                limit(SUBJECT_RESULTS_LIMIT)
                filter {
                    eq("org_id", orgId)
                    ilike("title", "%$trimmed%")
                }
            }
            .decodeList<DriveSubjectRow>()
            .map { ReminderSubjectOption("drive", it.id, it.title, "Drive") }
        return givers + drives
    }

    suspend fun create(orgId: String, reminder: NewReminder): ReminderSummary {
        val actor = authClient.requireCurrentUserId()
        val id = uuidV7()
        client.from(REMINDERS_TABLE).insert(
            ReminderInsert(
                id = id,
                orgId = orgId,
                subjectType = reminder.subjectType,
                subjectId = reminder.subjectId,
                description = reminder.description,
                assignedToUserId = reminder.assignedToUserId,
                dueAt = reminder.dueAt,
                recurrence = reminder.recurrence,
                emailEnabled = reminder.emailEnabled,
                createdByUserId = actor,
            ),
        )
        val row = client.from(REMINDERS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", id)
                }
            }
            .decodeSingle<ReminderRow>()
        return enrich(orgId, listOf(row)).single()
    }

    suspend fun complete(orgId: String, reminderId: String) {
        client.from(REMINDERS_TABLE).update(
            ReminderCompleteUpdate(completedAt = kotlin.time.Clock.System.now()),
        ) {
            filter {
                eq("org_id", orgId)
                eq("id", reminderId)
            }
        }
    }

    suspend fun snooze(orgId: String, reminderId: String, dueAt: Instant) {
        client.from(REMINDERS_TABLE).update(
            ReminderDueAtUpdate(dueAt = dueAt),
        ) {
            filter {
                eq("org_id", orgId)
                eq("id", reminderId)
            }
        }
    }

    suspend fun reassign(orgId: String, reminderId: String, userId: String) {
        client.from(REMINDERS_TABLE).update(
            ReminderAssigneeUpdate(assignedToUserId = userId),
        ) {
            filter {
                eq("org_id", orgId)
                eq("id", reminderId)
            }
        }
    }

    private suspend fun enrich(orgId: String, rows: List<ReminderRow>): List<ReminderSummary> {
        if (rows.isEmpty()) return emptyList()
        val giverIds = rows.filter { it.subjectType == "giver" }.map(ReminderRow::subjectId).distinct()
        val driveIds = rows.filter { it.subjectType == "drive" }.map(ReminderRow::subjectId).distinct()
        val reportIds = rows.filter { it.subjectType == "report" }.map(ReminderRow::subjectId).distinct()
        val givers = loadGivers(orgId, giverIds)
        val drives = loadDrives(orgId, driveIds)
        val reports = loadReports(orgId, reportIds)
        val assigneeNames = loadUserNames(rows.map(ReminderRow::assignedToUserId).distinct())
        val ruleNames = loadRuleNames(orgId, rows.mapNotNull(ReminderRow::createdByRuleId).distinct())
        return rows.map { row ->
            val (label, context) = when (row.subjectType) {
                "giver" -> givers[row.subjectId]?.let { it.displayName to "Giver · ${it.giverType}" }
                    ?: ("Unknown giver" to "Giver")
                "drive" -> drives[row.subjectId]?.let { it.title to "Drive" }
                    ?: ("Unknown drive" to "Drive")
                "report" -> reports[row.subjectId]?.let { it.title to "Report · ${it.status}" }
                    ?: ("Unknown report" to "Report")
                else -> (row.subjectId to row.subjectType)
            }
            ReminderSummary(
                id = row.id,
                subjectType = row.subjectType,
                subjectId = row.subjectId,
                subjectLabel = label,
                subjectContext = context,
                description = row.description,
                assignedToUserId = row.assignedToUserId,
                assignedToName = assigneeNames[row.assignedToUserId],
                dueAt = row.dueAt,
                completedAt = row.completedAt,
                isPriority = row.isPriority,
                recurrence = row.recurrence,
                createdByRuleId = row.createdByRuleId,
                ruleName = row.createdByRuleId?.let(ruleNames::get),
                emailEnabled = row.emailEnabled,
            )
        }
    }

    private suspend fun loadGivers(orgId: String, ids: List<String>): Map<String, GiverSubjectRow> {
        if (ids.isEmpty()) return emptyMap()
        return client.from(GIVERS_TABLE)
            .select { filter { eq("org_id", orgId); isIn("id", ids) } }
            .decodeList<GiverSubjectRow>()
            .associateBy(GiverSubjectRow::id)
    }

    private suspend fun loadDrives(orgId: String, ids: List<String>): Map<String, DriveSubjectRow> {
        if (ids.isEmpty()) return emptyMap()
        return client.from(DRIVES_TABLE)
            .select { filter { eq("org_id", orgId); isIn("id", ids) } }
            .decodeList<DriveSubjectRow>()
            .associateBy(DriveSubjectRow::id)
    }

    private suspend fun loadReports(orgId: String, ids: List<String>): Map<String, ReportSubjectRow> {
        if (ids.isEmpty()) return emptyMap()
        return client.from(REPORTS_TABLE)
            .select { filter { eq("org_id", orgId); isIn("id", ids) } }
            .decodeList<ReportSubjectRow>()
            .associateBy(ReportSubjectRow::id)
    }

    private suspend fun loadUserNames(userIds: List<String>): Map<String, String> {
        if (userIds.isEmpty()) return emptyMap()
        return client.from(PROFILES_TABLE)
            .select { filter { isIn("user_id", userIds) } }
            .decodeList<ProfileNameRow>()
            .associate { profile ->
                profile.userId to listOf(profile.firstName, profile.lastName)
                    .filter(String::isNotBlank)
                    .joinToString(" ")
            }
    }

    private suspend fun loadRuleNames(orgId: String, ruleIds: List<String>): Map<String, String> {
        if (ruleIds.isEmpty()) return emptyMap()
        return client.from(AUTOMATION_RULES_TABLE)
            .select { filter { eq("org_id", orgId); isIn("id", ruleIds) } }
            .decodeList<RuleNameRow>()
            .associate { it.id to it.name }
    }
}

@Serializable
private data class ReminderRow(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("subject_type")
    val subjectType: String,
    @SerialName("subject_id")
    val subjectId: String,
    val description: String,
    @SerialName("assigned_to_user_id")
    val assignedToUserId: String,
    @SerialName("due_at")
    val dueAt: Instant,
    @SerialName("completed_at")
    val completedAt: Instant?,
    @SerialName("is_priority")
    val isPriority: Boolean,
    val recurrence: String?,
    @SerialName("created_by_rule_id")
    val createdByRuleId: String?,
    @SerialName("email_enabled")
    val emailEnabled: Boolean = true,
)

@Serializable
private data class ReminderInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("subject_type")
    val subjectType: String,
    @SerialName("subject_id")
    val subjectId: String,
    val description: String,
    @SerialName("assigned_to_user_id")
    val assignedToUserId: String,
    @SerialName("due_at")
    val dueAt: Instant,
    val recurrence: String?,
    @SerialName("created_by_user_id")
    val createdByUserId: String,
    @SerialName("email_enabled")
    val emailEnabled: Boolean,
)

@Serializable
private data class ReminderCompleteUpdate(
    @SerialName("completed_at")
    val completedAt: Instant,
)

@Serializable
private data class ReminderDueAtUpdate(
    @SerialName("due_at")
    val dueAt: Instant,
)

@Serializable
private data class ReminderAssigneeUpdate(
    @SerialName("assigned_to_user_id")
    val assignedToUserId: String,
)

@Serializable
private data class GiverSubjectRow(
    val id: String,
    @SerialName("display_name")
    val displayName: String,
    @SerialName("giver_type")
    val giverType: String,
)

@Serializable
private data class DriveSubjectRow(
    val id: String,
    val title: String,
)

@Serializable
private data class ReportSubjectRow(
    val id: String,
    val title: String,
    val status: String,
)

@Serializable
private data class ProfileNameRow(
    @SerialName("user_id")
    val userId: String,
    @SerialName("first_name")
    val firstName: String,
    @SerialName("last_name")
    val lastName: String,
)

@Serializable
private data class RuleNameRow(
    val id: String,
    val name: String,
)

private const val REMINDERS_TABLE = "reminders"
private const val GIVERS_TABLE = "givers"
private const val DRIVES_TABLE = "drives"
private const val REPORTS_TABLE = "reports"
private const val PROFILES_TABLE = "profiles"
private const val AUTOMATION_RULES_TABLE = "automation_rules"
private const val LIST_LIMIT = 200L
private const val SUBJECT_RESULTS_LIMIT = 6L
